import React, { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from "@mui/material";
import {
  InfoOutlined,
  Search as SearchIcon,
  ChevronRightOutlined,
} from "@mui/icons-material";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import BaseSelect from "components/Shared/Base/BaseSelect";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useCustomParams } from "hooks/useCustomParams";
import HighlightedText from "components/Shared/HighlightedText";
import ParamsKeywordSearch from "components/Shared/ParamsKeywordSearch";

import {
  useLazyListShipmentStaleQuery,
  useLazyListShipmentStaleProvinceModeQuery,
} from "store/services/shipmentApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetDdlRegionListQuery } from "store/services/LocationsApi/regionApi";
import { useLazyGetBranchByRegionDDLQuery } from "store/services/branchApi";
import ExportStaleShipmentExcel from "./ExportStaleShipmentExcel";
import ExportStaleProvinceModeExcel from "./ExportStaleProvinceModeExcel";

const getStatusStyle = (status) => {
  const styles = {
    Pending: { backgroundColor: "#F8C471", textColor: "#D35400" },
    Collected: { backgroundColor: "#D4E6F1", textColor: "#2E86C1" },
    Loaded: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    Unloaded: { backgroundColor: "#FEF9E7", textColor: "#B7950B" },
    CrossDocked: { backgroundColor: "#E8DAEF", textColor: "#8E44AD" },
    SendOut: { backgroundColor: "#FADBD8", textColor: "#E74C3C" },
    Delivered: { backgroundColor: "#DFF7DF", textColor: "green" },
    DeliveryFailed: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  };
  return styles[status] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const MODE_TABS = [
  { label: "Province Mode", value: "provinces" },
  { label: "Shipment List Mode", value: "shipments" },
];

const UNATTENDED_SHIPMENTS_PATH = "/app/crm/reports/unattended-shipments";
const PROVINCES_HOME_PATH = `${UNATTENDED_SHIPMENTS_PATH}?mode=provinces`;

const PROVINCE_SORT_OPTIONS = [
  { label: "Shipment Count (Most)", value: "count:-1" },
  { label: "Shipment Count (Fewest)", value: "count:1" },
  { label: "Province Name (A-Z)", value: "name:1" },
  { label: "Province Name (Z-A)", value: "name:-1" },
];

const BRANCH_SORT_OPTIONS = [
  { label: "Shipment Count (Most)", value: "count:-1" },
  { label: "Shipment Count (Fewest)", value: "count:1" },
  { label: "Branch Name (A-Z)", value: "name:1" },
  { label: "Branch Name (Z-A)", value: "name:-1" },
];

const SHIPMENT_SORT_OPTIONS = [
  { label: "Created At (Newest First)", value: "createdAt:-1" },
  { label: "Created At (Oldest First)", value: "createdAt:1" },
  {
    label: "Unattended Duration (Longest)",
    value: "lastActivityDate:1",
  },
  {
    label: "Unattended Duration (Shortest)",
    value: "lastActivityDate:-1",
  },
];

const SHIPMENT_STATUS_OPTIONS = [
  { label: "All Statuses", value: "" },
  { label: "Collected", value: "Collected" },
  { label: "Loaded", value: "Loaded" },
  { label: "Unloaded", value: "Unloaded" },
  { label: "Deconsolidation", value: "Deconsolidation" },
  { label: "CrossDocked", value: "CrossDocked" },
  { label: "SendOut", value: "SendOut" },
  { label: "Warehousing", value: "Warehousing" },
  { label: "Return", value: "Return" },
  { label: "DeliveryFailed", value: "DeliveryFailed" },
];

const UNATTENDED_NOTE_TEXT =
  "These shipments have had no status change and no note added in the last 24 hours (48 hours for Loaded). The unattended duration is counted from the latest activity — the last status process, or the note if it was added after that.";

const PROVINCE_MODE_NOTE = `Note: ${UNATTENDED_NOTE_TEXT} Each shipment is counted under the branch it currently sits at — its active transit branch if it has one, otherwise its source branch (Pending, Collected, Consolidation) or destination branch (all other statuses).`;

const SHIPMENT_LIST_NOTE = `Note: ${UNATTENDED_NOTE_TEXT} Current Branch shows where each shipment sits now — active transit branch first, else source branch (Pending, Collected, Consolidation) or destination branch.`;

const NoteTooltip = ({ title, sx = {} }) => (
  <Tooltip
    arrow
    placement="bottom-start"
    title={
      <Typography variant="body2" sx={{ maxWidth: 340, whiteSpace: "normal" }}>
        {title}
      </Typography>
    }
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        cursor: "help",
        flexShrink: 0,
        width: "fit-content",
        ...sx,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        Note
      </Typography>
      <InfoOutlined fontSize="small" color="action" />
    </Box>
  </Tooltip>
);

const StaleShipmentListScreen = () => {
  const { isVendor, isCustomer, isVendorStaff, viewEntity } = useAuth();

  const [searchParams] = useSearchParams();
  const { params, appendParams, resetParams } = useCustomParams();

  // Mode toggle (Province vs Shipment List)
  const mode = params.mode || "provinces";

  // Province mode params
  const pmLevel = params.pmLevel || "provinces"; // provinces | branches | shipments
  const pmProvinceId = params.pmProvinceId || "";
  const pmProvinceName = params.pmProvinceName || "";
  const pmBranchId = params.pmBranchId || "";
  const pmBranchName = params.pmBranchName || "";
  const pmBranchKeyword = params.pmBranchKeyword || "";
  const pmShipmentKeyword = params.pmShipmentKeyword || "";
  const pmShipmentStatus = params.pmShipmentStatus || "";
  const pmSortOrder = params.pmSortOrder || "lastActivityDate:1";
  const pmSummarySortOrder = params.pmSummarySortOrder || "count:-1";

  // Shipment list mode params (preserve previous behavior)
  const shipmentStatus = params.shipmentStatus || "";
  // Default to "Unattended Duration (Longest)" so the most stale shipments
  // surface first — the typical reason to open this list.
  const sortOrder = params.sortOrder || "lastActivityDate:1";

  const sourceRegions = React.useMemo(
    () => params.sourceRegion?.split(",").filter(Boolean) ?? [],
    [params.sourceRegion]
  );

  const destinationRegions = React.useMemo(
    () => params.destinationRegion?.split(",").filter(Boolean) ?? [],
    [params.destinationRegion]
  );

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [triggerGetDDLBranchList, { data: branchesData }] =
    useLazyGetDDLBranchListQuery();

  const [triggerGetDdlRegionList, { data: regionsData }] =
    useLazyGetDdlRegionListQuery();

  const [triggerGetSourceBranchesByRegion] = useLazyGetBranchByRegionDDLQuery();

  const [triggerGetDestinationBranchesByRegion] =
    useLazyGetBranchByRegionDDLQuery();

  const sourceBranch = React.useMemo(
    () => params.sourceBranch?.split(",") ?? [],
    [params.sourceBranch]
  );
  const destinationBranch = React.useMemo(
    () => params.destinationBranch?.split(",") ?? [],
    [params.destinationBranch]
  );

  // Current Branch filter (branch the shipment currently sits at)
  const currentBranch = React.useMemo(
    () => params.currentBranch?.split(",").filter(Boolean) ?? [],
    [params.currentBranch]
  );

  // Current Province filter (province of the branch the shipment sits at)
  const currentRegion = React.useMemo(
    () => params.currentRegion?.split(",").filter(Boolean) ?? [],
    [params.currentRegion]
  );

  const mappedBranchList = React.useMemo(() => {
    const dataArray = Array.isArray(branchesData)
      ? branchesData
      : Array.isArray(branchesData?.data)
        ? branchesData.data
        : [];

    return dataArray
      .filter((item) => item?._id && item?.name && item?.code)
      .map((item) => ({
        label: `${item.name} - ${item.code}`,
        value: item._id,
      }));
  }, [branchesData]);

  const mappedRegionList = React.useMemo(() => {
    const dataArray = Array.isArray(regionsData)
      ? regionsData
      : Array.isArray(regionsData?.data)
        ? regionsData.data
        : [];

    return dataArray
      .filter((item) => item?._id && item?.name)
      .map((item) => ({
        label: item.name,
        value: item._id,
      }));
  }, [regionsData]);

  const [sourceRegionBranchesAccumulator, setSourceRegionBranchesAccumulator] =
    React.useState([]);
  const [
    destinationRegionBranchesAccumulator,
    setDestinationRegionBranchesAccumulator,
  ] = React.useState([]);

  const mappedSourceRegionBranches = React.useMemo(
    () =>
      Array.isArray(sourceRegionBranchesAccumulator)
        ? sourceRegionBranchesAccumulator
          .filter((item) => item?._id && item?.name && item?.code)
          .map((item) => ({
            label: `${item.name} - ${item.code}`,
            value: item._id,
          }))
        : [],
    [sourceRegionBranchesAccumulator]
  );

  const mappedDestinationRegionBranches = React.useMemo(
    () =>
      Array.isArray(destinationRegionBranchesAccumulator)
        ? destinationRegionBranchesAccumulator
          .filter((item) => item?._id && item?.name && item?.code)
          .map((item) => ({
            label: `${item.name} - ${item.code}`,
            value: item._id,
          }))
        : [],
    [destinationRegionBranchesAccumulator]
  );

  const selectedSource = React.useMemo(
    () =>
      mappedBranchList?.filter((branch) => sourceBranch.includes(branch.value)),
    [mappedBranchList, sourceBranch]
  );

  const selectedDestination = React.useMemo(
    () =>
      mappedBranchList?.filter((branch) =>
        destinationBranch.includes(branch.value)
      ),
    [mappedBranchList, destinationBranch]
  );

  const selectedCurrentBranch = React.useMemo(
    () =>
      mappedBranchList?.filter((branch) =>
        currentBranch.includes(branch.value)
      ),
    [mappedBranchList, currentBranch]
  );

  const selectedCurrentRegion = React.useMemo(
    () =>
      mappedRegionList?.filter((region) =>
        currentRegion.includes(region.value)
      ),
    [mappedRegionList, currentRegion]
  );

  const selectedSourceRegion = React.useMemo(
    () =>
      mappedRegionList?.filter((region) =>
        sourceRegions.includes(region.value)
      ),
    [mappedRegionList, sourceRegions]
  );

  const selectedDestinationRegion = React.useMemo(
    () =>
      mappedRegionList?.filter((region) =>
        destinationRegions.includes(region.value)
      ),
    [mappedRegionList, destinationRegions]
  );

  const sourceBranchIdsFromRegion = React.useMemo(
    () => mappedSourceRegionBranches.map((branch) => branch.value),
    [mappedSourceRegionBranches]
  );

  const destinationBranchIdsFromRegion = React.useMemo(
    () => mappedDestinationRegionBranches.map((branch) => branch.value),
    [mappedDestinationRegionBranches]
  );

  const baseBreadcrumbs = [
    {
      title: "Reports",
      path: "/app/crm/reports/list",
    },
    {
      title: "Unattended Shipments",
      ...(mode === "provinces" && { path: PROVINCES_HOME_PATH }),
    },
  ];

  const breadcrumbs = useMemo(() => {
    if (mode === "provinces" && pmLevel === "branches") {
      return [
        ...baseBreadcrumbs,
        { title: "Province", path: PROVINCES_HOME_PATH },
      ];
    }

    if (mode === "provinces" && pmLevel === "shipments") {
      const branchListPath = `${UNATTENDED_SHIPMENTS_PATH}?mode=provinces&pmLevel=branches&pmProvinceId=${pmProvinceId}&pmProvinceName=${encodeURIComponent(
        pmProvinceName
      )}`;

      return [
        ...baseBreadcrumbs,
        { title: "Province", path: PROVINCES_HOME_PATH },
        { title: "Branch", path: branchListPath },
        { title: "Shipments" },
      ];
    }

    return baseBreadcrumbs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, pmLevel, pmProvinceId, pmProvinceName]);

  // ---------- Shipment list columns (used by both Shipment List mode & Province mode shipments level) ----------
  const buildShipmentColumns = (keyword = "") => [
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentId,
      width: "220px",
      cell: (row) => {
        const shiId = row?.shipmentId?.replace(/(.{4})/g, "$1-").slice(0, -1);
        const displayId = `${shiId}${row.IsReturning ? " (R)" : ""}`;
        const path = `/app/${isVendor
          ? "vendor"
          : isCustomer
            ? "customer"
            : isVendorStaff
              ? "staff"
              : "crm"
          }/shipment/${row._id}/detail`;

        return (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {displayId ? (
              <HighlightedText text={displayId} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </Link>
        );
      },
    },
    {
      name: "Receiver",
      width: "200px",
      selector: (row) => row?.receiver?.customerUser?.name,
      cell: (row) => (
        <div>
          <span className="text-sm">{row?.receiver?.customerUser?.name}</span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.receiver?.phone}
          </span>
        </div>
      ),
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch?.name,
      cell: (row) => row?.sourceBranch?.name || "N/A",
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch?.name,
      cell: (row) => row?.destinationBranch?.name || "N/A",
    },
    {
      name: "Current Branch",
      width: "170px",
      selector: (row) => row?.currentBranch?.name,
      cell: (row) => (
        <Box>
          <Typography variant="body2">
            {row?.currentBranch?.name || "N/A"}
          </Typography>
          {row?.currentBranchType && (
            <Chip
              label={row.currentBranchType}
              size="small"
              variant="outlined"
              sx={{
                height: "16px",
                borderRadius: "8px",
                "& .MuiChip-label": {
                  fontSize: "0.6rem",
                  padding: "0 5px",
                  lineHeight: "14px",
                },
              }}
            />
          )}
        </Box>
      ),
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Created By",
      selector: (row) => row?.createdBy?.name || "N/A",
      cell: (row) => row?.createdBy?.name || "N/A",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Chip
          label={row?.status?.toUpperCase() || "N/A"}
          variant="outlined"
          sx={{
            backgroundColor: getStatusStyle(row?.status).backgroundColor,
            color: getStatusStyle(row?.status).textColor,
            fontWeight: "bold",
            height: "17px",
            borderRadius: "12px",
            width: "auto",
            padding: "0",
            border: "none",
            "& .MuiChip-label": {
              fontSize: "0.6rem",
              padding: "3px 5px",
              lineHeight: "14px",
            },
          }}
        />
      ),
    },
    {
      name: "Unattended Duration",
      selector: (row) => row.hoursSinceLastProcess,
      width: "150px",
      cell: (row) => {
        const hours = Math.floor(row.hoursSinceLastProcess);
        const days = Math.floor(hours / 24);
        return (
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "error.main" }}
            >
              {days > 0 ? `${days}d ${hours % 24}h` : `${hours}h`}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              No activity
            </Typography>
          </Box>
        );
      },
    },
    {
      name: "Actions",
      cell: (row) => {
        const basePath = isVendor
          ? "/app/vendor/shipment"
          : isCustomer
            ? "/app/customer/shipment"
            : isVendorStaff
              ? "/app/staff/shipment"
              : "/app/crm/shipment";
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
          >
            <Link to={`${basePath}/${row?._id}/detail`}>
              <IconButton
                size="small"
                aria-label="View Shipment"
                color="primary"
              >
                <InfoOutlined />
              </IconButton>
            </Link>
          </Box>
        );
      },
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchStaleShipment,
    { data: shipmentRes, isLoading, isFetching, error },
  ] = useLazyListShipmentStaleQuery();

  // Province mode API
  const [
    fetchProvinceMode,
    {
      data: provinceModeRes,
      isLoading: pmLoading,
      isFetching: pmFetching,
      error: pmError,
    },
  ] = useLazyListShipmentStaleProvinceModeQuery();

  const fetchBranches = () => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetDDLBranchList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      })
        .unwrap()
        .catch((error) => {
          console.error("Failed to fetch branches:", error);
        });
    }
  };

  const fetchRegions = () => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetDdlRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      })
        .unwrap()
        .catch((error) => {
          console.error("Failed to fetch regions:", error);
        });
    } else {
      console.warn("Missing account or entity ID for region fetch");
    }
  };

  const handleSourceRegionChange = (_, selectedRegions) => {
    const regionIds = Array.isArray(selectedRegions)
      ? selectedRegions.map((region) => region.value).join(",")
      : "";

    appendParams({
      sourceRegion: regionIds,
      sourceBranch: "",
      page: 1,
    });
  };

  const handleDestinationRegionChange = (_, selectedRegions) => {
    const regionIds = Array.isArray(selectedRegions)
      ? selectedRegions.map((region) => region.value).join(",")
      : "";

    appendParams({
      destinationRegion: regionIds,
      destinationBranch: "",
      page: 1,
    });
  };

  useEffect(() => {
    if (
      (sourceRegions.length > 0 || destinationRegions.length > 0) &&
      viewEntity?.entity?.account?._id &&
      viewEntity?.entity?._id
    ) {
      triggerGetDdlRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      })
        .unwrap()
        .catch((error) => {
          console.error("Failed to fetch regions on mount:", error);
        });
    }
  }, [
    sourceRegions.length,
    destinationRegions.length,
    viewEntity.entity.account._id,
    viewEntity.entity._id,
    triggerGetDdlRegionList,
  ]);

  useEffect(() => {
    if (
      sourceRegions.length === 0 ||
      !viewEntity?.entity?.account?._id ||
      !viewEntity?.entity?._id
    ) {
      setSourceRegionBranchesAccumulator([]);
      return;
    }

    let cancelled = false;
    Promise.allSettled(
      sourceRegions.map((regionId) =>
        triggerGetSourceBranchesByRegion({
          regionId,
          account: viewEntity.entity.account._id,
          entity: viewEntity.entity._id,
        }).unwrap()
      )
    ).then((results) => {
      if (cancelled) return;
      const merged = results
        .filter((result) => result.status === "fulfilled")
        .flatMap((result) =>
          Array.isArray(result.value?.data) ? result.value.data : []
        );
      const dedupedMap = new Map();
      merged.forEach((branch) => {
        if (branch?._id && !dedupedMap.has(branch._id)) {
          dedupedMap.set(branch._id, branch);
        }
      });
      setSourceRegionBranchesAccumulator(Array.from(dedupedMap.values()));
    });

    return () => {
      cancelled = true;
    };
  }, [
    sourceRegions,
    viewEntity.entity.account._id,
    viewEntity.entity._id,
    triggerGetSourceBranchesByRegion,
  ]);

  useEffect(() => {
    if (
      destinationRegions.length === 0 ||
      !viewEntity?.entity?.account?._id ||
      !viewEntity?.entity?._id
    ) {
      setDestinationRegionBranchesAccumulator([]);
      return;
    }

    let cancelled = false;
    Promise.allSettled(
      destinationRegions.map((regionId) =>
        triggerGetDestinationBranchesByRegion({
          regionId,
          account: viewEntity.entity.account._id,
          entity: viewEntity.entity._id,
        }).unwrap()
      )
    ).then((results) => {
      if (cancelled) return;
      const merged = results
        .filter((result) => result.status === "fulfilled")
        .flatMap((result) =>
          Array.isArray(result.value?.data) ? result.value.data : []
        );
      const dedupedMap = new Map();
      merged.forEach((branch) => {
        if (branch?._id && !dedupedMap.has(branch._id)) {
          dedupedMap.set(branch._id, branch);
        }
      });
      setDestinationRegionBranchesAccumulator(Array.from(dedupedMap.values()));
    });

    return () => {
      cancelled = true;
    };
  }, [
    destinationRegions,
    viewEntity.entity.account._id,
    viewEntity.entity._id,
    triggerGetDestinationBranchesByRegion,
  ]);

  useEffect(() => {
    if (
      (sourceBranch.length > 0 || destinationBranch.length > 0) &&
      viewEntity?.entity?.account?._id &&
      viewEntity?.entity?._id
    ) {
      triggerGetDDLBranchList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      })
        .unwrap()
        .catch((error) => {
          console.error("Failed to fetch branches on mount:", error);
        });
    }
  }, [
    sourceBranch.length,
    destinationBranch.length,
    viewEntity.entity.account._id,
    viewEntity.entity._id,
    triggerGetDDLBranchList,
  ]);

  // Shipment List Mode fetch
  const keyword = searchParams.get("search") || "";

  useEffect(() => {
    if (mode !== "shipments") return;

    // Guard against re-firing while a previous request is still in flight —
    // the stale-shipment aggregation is expensive, and stacking queries just
    // multiplies server load without any benefit to the user.
    if (isLoading || isFetching) return;

    const [sortedBy, sortDirection] = sortOrder.split(":");
    const cleanedKeyword = keyword ? keyword.replace(/-/g, "") : "";

    const finalSourceBranchIds =
      sourceRegions.length > 0 ? sourceBranchIdsFromRegion : sourceBranch;
    const finalDestinationBranchIds =
      destinationRegions.length > 0
        ? destinationBranchIdsFromRegion
        : destinationBranch;

    fetchStaleShipment({
      page: currentPage,
      limit: perPage,
      ...(cleanedKeyword && { keyword: cleanedKeyword }),
      sortedBy,
      sortDirection: parseInt(sortDirection),
      ...(!!finalDestinationBranchIds?.length && {
        destinationBranchId: finalDestinationBranchIds,
      }),
      ...(!!finalSourceBranchIds?.length && {
        sourceBranchId: finalSourceBranchIds,
      }),
      ...(!!currentBranch?.length && { currentBranchId: currentBranch }),
      ...(!!currentRegion?.length && { currentRegionId: currentRegion }),
      ...(shipmentStatus && { status: shipmentStatus }),
    });
  }, [
    mode,
    currentPage,
    fetchStaleShipment,
    keyword,
    perPage,
    destinationBranch,
    sourceBranch,
    sourceRegions,
    destinationRegions,
    sourceBranchIdsFromRegion,
    destinationBranchIdsFromRegion,
    currentBranch,
    currentRegion,
    shipmentStatus,
    sortOrder,
  ]);

  const [summarySortedBy, summarySortDirection] = pmSummarySortOrder.split(":");
  const [pmShipmentSortedBy, pmShipmentSortDirection] = pmSortOrder.split(":");
  const cleanedPmShipmentKeyword = pmShipmentKeyword
    ? pmShipmentKeyword.replace(/-/g, "")
    : "";

  // Province Mode fetch
  useEffect(() => {
    if (mode !== "provinces") return;

    // Guard against re-firing while a previous request is still in flight.
    if (pmLoading || pmFetching) return;

    if (pmLevel === "provinces") {
      fetchProvinceMode({
        level: "provinces",
        ...(pmShipmentStatus && { status: pmShipmentStatus }),
        sortedBy: summarySortedBy,
        sortDirection: parseInt(summarySortDirection),
      });
      return;
    }

    if (pmLevel === "branches" && pmProvinceId) {
      fetchProvinceMode({
        level: "branches",
        provinceId: pmProvinceId,
        ...(pmShipmentStatus && { status: pmShipmentStatus }),
        branchKeyword: pmBranchKeyword,
        sortedBy: summarySortedBy,
        sortDirection: parseInt(summarySortDirection),
        page: currentPage,
        limit: perPage,
      });
      return;
    }

    if (pmLevel === "shipments" && pmBranchId) {
      fetchProvinceMode({
        level: "shipments",
        branchId: pmBranchId,
        ...(cleanedPmShipmentKeyword && {
          keyword: cleanedPmShipmentKeyword,
        }),
        ...(pmShipmentStatus && { status: pmShipmentStatus }),
        sortedBy: pmShipmentSortedBy,
        sortDirection: parseInt(pmShipmentSortDirection),
        page: currentPage,
        limit: perPage,
      });
    }
  }, [
    mode,
    pmLevel,
    pmProvinceId,
    pmBranchId,
    pmBranchKeyword,
    pmShipmentKeyword,
    pmShipmentStatus,
    pmSortOrder,
    pmSummarySortOrder,
    summarySortedBy,
    summarySortDirection,
    pmShipmentSortedBy,
    pmShipmentSortDirection,
    cleanedPmShipmentKeyword,
    currentPage,
    perPage,
    fetchProvinceMode,
  ]);

  // ----- Mode tab change -----
  const handleModeTabChange = (_, value) => {
    if (!value || value === mode) return;

    appendParams({
      mode: value,
      page: null,
      ...(value === "shipments" && {
        pmLevel: null,
        pmProvinceId: null,
        pmProvinceName: null,
        pmBranchId: null,
        pmBranchName: null,
        pmBranchKeyword: null,
        pmShipmentKeyword: null,
        pmShipmentStatus: null,
        pmSortOrder: null,
        pmSummarySortOrder: null,
      }),
    });
  };

  // ----- Drilldown handlers -----
  const handleDrillIntoBranches = (row) => {
    appendParams({
      mode: "provinces",
      pmLevel: "branches",
      pmProvinceId: row.provinceId || row._id,
      pmProvinceName: row.name,
      pmBranchId: null,
      pmBranchName: null,
      pmBranchKeyword: null,
      pmShipmentKeyword: null,
      page: 1,
    });
  };

  const handleDrillIntoShipments = (row) => {
    appendParams({
      mode: "provinces",
      pmLevel: "shipments",
      pmProvinceId,
      pmProvinceName,
      pmBranchId: row._id,
      pmBranchName: `${row.name}${row.code ? ` (${row.code})` : ""}`,
      pmShipmentKeyword: null,
      page: 1,
    });
  };

  // Memoized columns capture first-render closures; route drill clicks
  // through a ref so the handlers always use the latest URL params.
  const drillHandlersRef = React.useRef({});
  drillHandlersRef.current = {
    handleDrillIntoBranches,
    handleDrillIntoShipments,
  };

  // Province mode - provinces level columns
  const provinceColumns = useMemo(
    () => [
      {
        name: "Province",
        selector: (row) => row.name,
        cell: (row) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.name || "N/A"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {row.code || ""}
            </Typography>
          </Box>
        ),
        sortable: true,
      },
      {
        name: "Stale Shipments",
        width: "180px",
        selector: (row) => row.count,
        cell: (row) => (
          <Chip
            label={row.count ?? 0}
            color={row.count > 0 ? "warning" : "default"}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        ),
        sortable: true,
      },
      {
        name: "Actions",
        width: "220px",
        cell: (row) => (
          <Box sx={{ pl: 2, pr: 1 }}>
            <Button
              size="small"
              variant="outlined"
              endIcon={<ChevronRightOutlined />}
              onClick={() =>
                drillHandlersRef.current.handleDrillIntoBranches(row)
              }
              sx={{ whiteSpace: "nowrap" }}
            >
              View Branches
            </Button>
          </Box>
        ),
        ignoreRowClick: true,
        right: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const branchColumns = useMemo(
    () => [
      {
        name: "Branch",
        selector: (row) => row.name,
        cell: (row) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.name || "N/A"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {row.code || ""}
            </Typography>
          </Box>
        ),
        sortable: true,
      },
      {
        name: "Stale Shipments",
        width: "180px",
        selector: (row) => row.count,
        cell: (row) => (
          <Chip
            label={row.count ?? 0}
            color={row.count > 0 ? "warning" : "default"}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        ),
        sortable: true,
      },
      {
        name: "Actions",
        width: "220px",
        cell: (row) => (
          <Box sx={{ m: 1 }}>
            <Button
              size="small"
              variant="outlined"
              endIcon={<ChevronRightOutlined />}
              onClick={() =>
                drillHandlersRef.current.handleDrillIntoShipments(row)
              }
              sx={{ whiteSpace: "nowrap" }}
            >
              View Shipments
            </Button>
          </Box>
        ),
        ignoreRowClick: true,
        right: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Client-side province search (provinces level only - backend doesn't filter)
  const [provinceSearch, setProvinceSearch] = React.useState("");
  const provinceData = useMemo(() => {
    const rawData = provinceModeRes?.data ?? [];
    if (!provinceSearch.trim()) return rawData;
    const lower = provinceSearch.trim().toLowerCase();
    return rawData.filter(
      (p) =>
        p?.name?.toLowerCase().includes(lower) ||
        p?.code?.toLowerCase().includes(lower)
    );
  }, [provinceModeRes, provinceSearch]);

  const provinceBranchSummary = useMemo(() => {
    if (pmLevel !== "branches") return null;

    return {
      totalBranches: provinceModeRes?.totalItems ?? 0,
      totalShipments:
        provinceModeRes?.totalShipments ??
        (provinceModeRes?.data ?? []).reduce(
          (sum, branch) => sum + (branch?.count || 0),
          0
        ),
    };
  }, [pmLevel, provinceModeRes]);

  const provinceShipmentColumns = useMemo(
    () => buildShipmentColumns(pmShipmentKeyword),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pmShipmentKeyword, isVendor, isCustomer, isVendorStaff]
  );

  const shipmentListColumns = useMemo(
    () => buildShipmentColumns(keyword),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [keyword, isVendor, isCustomer, isVendorStaff]
  );

  // ----- Renderers -----
  const renderProvinceModeContent = () => {
    if (pmLevel === "branches") {
      return (
        <Box>
          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ p: 2, flexWrap: "wrap" }}
          >
            <Box
              sx={{
                pr: 2,
                borderRight: 1,
                borderColor: "divider",
                minWidth: 260,
                mr: "auto",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
                noWrap
              >
                {pmProvinceName || "Province"}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 1 }}>
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${provinceBranchSummary?.totalBranches ?? 0
                    } Branches`}
                />
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`${provinceBranchSummary?.totalShipments ?? 0
                    } Shipments`}
                />
              </Stack>
              <NoteTooltip title={PROVINCE_MODE_NOTE} sx={{ mt: 0.75 }} />
            </Box>
            <BaseSelect
              size="small"
              label="Status"
              value={pmShipmentStatus}
              onChange={(e) =>
                appendParams({
                  pmShipmentStatus: e.target.value,
                  page: 1,
                })
              }
              sx={{ minWidth: 180 }}
              options={SHIPMENT_STATUS_OPTIONS}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
            />
            <BaseSelect
              size="small"
              label="Sort By"
              value={pmSummarySortOrder}
              onChange={(e) =>
                appendParams({
                  pmSummarySortOrder: e.target.value,
                  page: 1,
                })
              }
              sx={{ minWidth: 180 }}
              options={BRANCH_SORT_OPTIONS}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
            />
            <ParamsKeywordSearch
              label="Search branch by name or code"
              paramKey="pmBranchKeyword"
              minLength={1}
              debounceDelay={500}
              disabled={pmLoading || pmFetching}
              sx={{ minWidth: 280 }}
            />
            <ExportStaleProvinceModeExcel
              level="branches"
              scopeName={pmProvinceName}
              provinceName={pmProvinceName}
              isBusy={pmLoading || pmFetching}
              requestParams={{
                level: "branches",
                provinceId: pmProvinceId,
                ...(pmShipmentStatus && { status: pmShipmentStatus }),
                branchKeyword: pmBranchKeyword,
                sortedBy: summarySortedBy,
                sortDirection: parseInt(summarySortDirection),
              }}
            />
          </Stack>

          <AppTable
            columns={branchColumns}
            data={provinceModeRes?.data ?? []}
            progressPending={pmLoading || pmFetching}
            onRowClicked={handleDrillIntoShipments}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={provinceModeRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            noDataComponent={
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6">
                  No branches with stale shipments in {pmProvinceName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "text.secondary" }}
                >
                  Try adjusting your search or check back later.
                </Typography>
              </Box>
            }
          />
        </Box>
      );
    }

    if (pmLevel === "shipments") {
      return (
        <Box>
          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ p: 2, flexWrap: "wrap" }}
          >
            <Box
              sx={{
                pr: 2,
                borderRight: 1,
                borderColor: "divider",
                minWidth: 220,
                mr: "auto",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
                noWrap
              >
                {pmBranchName || "Branch"}
              </Typography>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                sx={{ mt: 0.5 }}
                label={`${provinceModeRes?.totalItems ?? 0
                  } Unattended Shipments`}
              />
              <NoteTooltip title={SHIPMENT_LIST_NOTE} sx={{ mt: 0.75 }} />
            </Box>
            <BaseSelect
              size="small"
              label="Status"
              value={pmShipmentStatus}
              onChange={(e) =>
                appendParams({
                  pmShipmentStatus: e.target.value,
                  page: 1,
                })
              }
              sx={{ minWidth: 180 }}
              options={SHIPMENT_STATUS_OPTIONS}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
            />
            <BaseSelect
              size="small"
              label="Sort By"
              value={pmSortOrder}
              onChange={(e) =>
                appendParams({ pmSortOrder: e.target.value, page: 1 })
              }
              sx={{ minWidth: 180 }}
              options={SHIPMENT_SORT_OPTIONS}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
            />
            <ParamsKeywordSearch
              label="Search by shipment ID"
              paramKey="pmShipmentKeyword"
              minLength={1}
              debounceDelay={500}
              disabled={pmLoading || pmFetching}
              sx={{ minWidth: 240 }}
            />
            <ExportStaleProvinceModeExcel
              level="shipments"
              scopeName={pmBranchName || pmProvinceName}
              provinceName={pmProvinceName}
              branchName={pmBranchName}
              isBusy={pmLoading || pmFetching}
              requestParams={{
                level: "shipments",
                branchId: pmBranchId,
                ...(cleanedPmShipmentKeyword && {
                  keyword: cleanedPmShipmentKeyword,
                }),
                ...(pmShipmentStatus && { status: pmShipmentStatus }),
                sortedBy: pmShipmentSortedBy,
                sortDirection: parseInt(pmShipmentSortDirection),
              }}
            />
          </Stack>

          <AppTable
            columns={provinceShipmentColumns}
            data={provinceModeRes?.data ?? []}
            progressPending={pmLoading || pmFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={provinceModeRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            noDataComponent={
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6">
                  No stale shipments found for this branch
                </Typography>
              </Box>
            }
          />
        </Box>
      );
    }

    // Provinces level
    return (
      <Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ p: 2, pb: 1, flexWrap: "wrap", rowGap: 2 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              mr: "auto",
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "warning.lighter",
                borderLeft: "4px solid",
                borderRight: "4px solid",
                borderColor: "warning.main",
                flexShrink: 0,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                gutterBottom
                display="block"
                lineHeight={1.2}
              >
                Total Unattended Shipments
              </Typography>
              <Typography variant="h5" fontWeight="400" color="warning.dark">
                {(provinceModeRes?.data ?? []).reduce(
                  (sum, p) => sum + (p?.count || 0),
                  0
                )}
              </Typography>
            </Box>
            <NoteTooltip title={PROVINCE_MODE_NOTE} />
          </Box>

          <BaseSelect
            size="small"
            label="Status"
            value={pmShipmentStatus}
            onChange={(e) =>
              appendParams({
                pmShipmentStatus: e.target.value,
                page: 1,
              })
            }
            sx={{ minWidth: 180 }}
            options={SHIPMENT_STATUS_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
          <BaseSelect
            size="small"
            label="Sort By"
            value={pmSummarySortOrder}
            onChange={(e) =>
              appendParams({
                pmSummarySortOrder: e.target.value,
                page: 1,
              })
            }
            sx={{ minWidth: 180 }}
            options={PROVINCE_SORT_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
          <TextField
            size="small"
            placeholder="Search province by name or code"
            value={provinceSearch}
            onChange={(e) => setProvinceSearch(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <ExportStaleProvinceModeExcel
            level="provinces"
            search={provinceSearch}
            isBusy={pmLoading || pmFetching}
            requestParams={{
              level: "provinces",
              ...(pmShipmentStatus && { status: pmShipmentStatus }),
              sortedBy: summarySortedBy,
              sortDirection: parseInt(summarySortDirection),
            }}
          />
        </Stack>

        <AppTable
          columns={provinceColumns}
          data={provinceData}
          progressPending={pmLoading || pmFetching}
          onRowClicked={handleDrillIntoBranches}
          pagination={false}
          noDataComponent={
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6">
                No provinces with stale shipments
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 1, color: "text.secondary" }}
              >
                All shipments have had recent activity in the last 24 hours.
              </Typography>
            </Box>
          }
        />
      </Box>
    );
  };

  const renderShipmentListModeContent = () => (
    <>
      <TableFilter
        searchLabel="Shipment ID"
        disabled={isLoading || isFetching}
        initialFilters={3}
        exportComponent={ExportStaleShipmentExcel}
        onClear={() => resetParams({ mode: "shipments" })}
        startContent={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "warning.lighter",
                borderLeft: "4px solid",
                borderRight: "4px solid",
                borderColor: "warning.main",
                flexShrink: 0,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                gutterBottom
                display="block"
                lineHeight={1.2}
              >
                Current Unattended Shipments
              </Typography>
              <Typography variant="h5" fontWeight="400" color="warning.dark">
                {shipmentRes?.totalItems ?? 0}
              </Typography>
            </Box>
            <NoteTooltip title={SHIPMENT_LIST_NOTE} />
          </Box>
        }
      >
        <BaseAutocomplete
          fullWidth
          multiple
          label="Source Branch"
          value={selectedSource}
          options={mappedBranchList}
          limitTags={2}
          onChange={(_, data) => {
            appendParams({
              sourceBranch: data.map((branch) => branch.value).join(","),
              sourceRegion: "",
              page: 1,
            });
          }}
          onFocus={() => {
            if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
              triggerGetDDLBranchList({
                account: viewEntity.entity.account._id,
                entity: viewEntity.entity._id,
              })
                .unwrap()
                .catch((error) => {
                  console.error("Failed to fetch branches:", error);
                });
            }
          }}
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
        />

        <BaseAutocomplete
          fullWidth
          multiple
          label="Destination Branch"
          value={selectedDestination}
          limitTags={2}
          options={mappedBranchList}
          onChange={(_, data) => {
            appendParams({
              destinationBranch: data.map((branch) => branch.value).join(","),
              destinationRegion: "",
              page: 1,
            });
          }}
          onFocus={() => {
            if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
              triggerGetDDLBranchList({
                account: viewEntity.entity.account._id,
                entity: viewEntity.entity._id,
              })
                .unwrap()
                .catch((error) => {
                  console.error("Failed to fetch branches:", error);
                });
            }
          }}
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
        />

        <BaseAutocomplete
          fullWidth
          multiple
          label="Current Branch (At Branch)"
          value={selectedCurrentBranch}
          options={mappedBranchList}
          limitTags={2}
          onChange={(_, data) => {
            appendParams({
              currentBranch: data.map((branch) => branch.value).join(","),
              page: 1,
            });
          }}
          onFocus={fetchBranches}
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
        />

        <BaseAutocomplete
          fullWidth
          multiple
          label="Current Province (At Province)"
          value={selectedCurrentRegion || []}
          options={mappedRegionList}
          limitTags={2}
          onChange={(_, data) => {
            appendParams({
              currentRegion: data.map((region) => region.value).join(","),
              page: 1,
            });
          }}
          onFocus={fetchRegions}
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
        />

        <BaseSelect
          fullWidth
          label="Shipment Status"
          value={shipmentStatus}
          onChange={(e) => {
            appendParams({
              shipmentStatus: e.target.value,
              page: 1,
            });
          }}
          options={[
            { label: "All Statuses", value: "" },
            { label: "Collected", value: "Collected" },
            { label: "Loaded", value: "Loaded" },
            { label: "Unloaded", value: "Unloaded" },
            { label: "Deconsolidation", value: "Deconsolidation" },
            { label: "CrossDocked", value: "CrossDocked" },
            { label: "SendOut", value: "SendOut" },
            { label: "Warehousing", value: "Warehousing" },
            { label: "Return", value: "Return" },
            { label: "DeliveryFailed", value: "DeliveryFailed" },
          ]}
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => opt.value}
        />

        <BaseAutocomplete
          fullWidth
          multiple
          label="Source Province"
          value={selectedSourceRegion || []}
          limitTags={2}
          options={mappedRegionList}
          onChange={handleSourceRegionChange}
          onFocus={fetchRegions}
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
        />

        <BaseAutocomplete
          fullWidth
          multiple
          label="Destination Province"
          value={selectedDestinationRegion || []}
          limitTags={2}
          options={mappedRegionList}
          onChange={handleDestinationRegionChange}
          onFocus={fetchRegions}
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
        />

        <BaseSelect
          fullWidth
          label="Sort By"
          value={sortOrder}
          onChange={(e) => {
            appendParams({
              sortOrder: e.target.value,
              page: 1,
            });
          }}
          options={[
            { label: "Created At (Newest First)", value: "createdAt:-1" },
            { label: "Created At (Oldest First)", value: "createdAt:1" },
            {
              label: "Unattended Duration (Longest)",
              value: "lastActivityDate:1",
            },
            {
              label: "Unattended Duration (Shortest)",
              value: "lastActivityDate:-1",
            },
          ]}
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => opt.value}
        />
      </TableFilter>

      {(sourceRegions.length > 0 && sourceBranchIdsFromRegion.length === 0) ||
        (destinationRegions.length > 0 &&
          destinationBranchIdsFromRegion.length === 0) ? (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            color: "text.secondary",
          }}
        >
          <Typography variant="h6">
            No shipments found in this region
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            The selected region doesn't have any branches with shipments.
          </Typography>
        </Box>
      ) : (
        <AppTable
          columns={shipmentListColumns}
          data={shipmentRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={shipmentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          noDataComponent={
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6">
                {sourceRegions.length > 0 || destinationRegions.length > 0
                  ? "No stale shipments found in this region"
                  : "No stale shipments found"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 1, color: "text.secondary" }}
              >
                All shipments have had recent activity in the last 24 hours
              </Typography>
            </Box>
          }
        />
      )}
    </>
  );

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      headerActions={
        <Tabs
          value={mode}
          onChange={handleModeTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 36,
            "& .MuiTab-root": { minHeight: 36, py: 0 },
          }}
        >
          {MODE_TABS.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>
      }
    >
      <BreadcrumbLayout.Error error={mode === "shipments" ? error : pmError} />
      <BreadcrumbLayout.Paper>
        {mode === "provinces" && <Box>{renderProvinceModeContent()}</Box>}

        {mode === "shipments" && <Box>{renderShipmentListModeContent()}</Box>}
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default StaleShipmentListScreen;
